<template>
  <div class="youtube-thumbnail-generator">
    <h1 class="bold">
      Youtube Thumbnail Generator 
    </h1>

    <p v-if="isFetching" class="loading-message">
      <span class="spinner" /> Fetching data...
    </p>

    <div v-else>
      <!-- Prompts Configuration Section -->
      <div class="prompts-section">
        <h2 class="section-title">Configure AI Prompts</h2>
        <p class="section-description">Customize the prompts used to generate different styles of thumbnails</p>
        
        <form @submit.prevent="handleSavePrompts" class="prompts-form">
          <div class="prompts-grid">
            <div class="prompt-group">
              <BaseInput 
                name="promptGenerateTitleFromYoutube"
                label="Extract Title Prompt"
                :disabled="isSaving || isGenerating" 
                placeholder="Provide title prompt"
                type="textarea" 
                :value="promptGenerateTitleFromYoutube"
                @input="promptGenerateTitleFromYoutube = $event.value"
              />
            </div>

            <div class="prompt-group">
              <BaseInput 
                name="promptImageGenerate" 
                label="🎨 Brand custom prompt"
                :disabled="isSaving || isGenerating" 
                placeholder="Provide base image generation prompt" 
                type="textarea" 
                :value="promptImageGenerate"
                @input="promptImageGenerate = $event.value"
              />
            </div>

            <div class="prompt-group">
              <BaseInput 
                name="promptFaceCentric" 
                label="👤 Face-Centric Prompt" 
                :disabled="isSaving || isGenerating" 
                placeholder="Prompt for face-focused thumbnails" 
                type="textarea" 
                :value="promptFaceCentric"
                @input="promptFaceCentric = $event.value"
              />
            </div>

            <div class="prompt-group">
              <BaseInput 
                name="promptCuriosityHook" 
                label="🤔 Curiosity Hook Prompt" 
                :disabled="isSaving || isGenerating" 
                placeholder="Prompt for curiosity-driven thumbnails" 
                type="textarea" 
                :value="promptCuriosityHook"
                @input="promptCuriosityHook = $event.value"
              />
            </div>

            <div class="prompt-group">
              <BaseInput 
                name="promptCinematic" 
                label="🎬 Cinematic Prompt" 
                :disabled="isSaving || isGenerating" 
                placeholder="Prompt for cinematic-style thumbnails" 
                type="textarea" 
                :value="promptCinematic"
                @input="promptCinematic = $event.value"
              />
            </div>

            <div class="prompt-group">
              <BaseInput 
                name="promptCollage" 
                label="🖼️ Collage Prompt" 
                :disabled="isSaving || isGenerating" 
                placeholder="Prompt for collage-style thumbnails" 
                type="textarea" 
                :value="promptCollage"
                @input="promptCollage = $event.value"
              />
            </div>

            <div class="prompt-group">
              <BaseInput 
                name="promptMinimalistHeadshot" 
                label="✨ Minimalist Headshot Prompt" 
                :disabled="isSaving || isGenerating" 
                placeholder="Prompt for minimalist headshot thumbnails" 
                type="textarea" 
                :value="promptMinimalistHeadshot"
                @input="promptMinimalistHeadshot = $event.value"
              />
            </div>
          </div>

          <div class="form-actions">
            <button class="purple-btn save-btn" 
                    :disabled="isSaving || isGenerating" 
                    type="submit">
                <span class="spinner" v-if="isSaving"/>
                <span class="btn-text">{{isSaving ? 'Saving...' : 'Save All Prompts'}}</span>
            </button>
          </div>
        </form>
      </div>

      <!-- Thumbnail Generation Section -->
      <div class="generation-section">
        <h2 class="section-title">Generate Thumbnail</h2>
        <p class="section-description">Enter your video details to generate a custom thumbnail</p>
        
        <form @submit.prevent="handleGenerateThumbnail" class="generation-form">
          <div class="input-group">
            <BaseInput 
              name="title" 
              label="📹 Video Title" 
              :disabled="isGenerating || isSaving" 
              placeholder="Enter your video title"
              :value="title"
              @input="title = $event.value"
            />
            <BaseInput 
              name="domain" 
              label="🏷️ Content Domain/Category" 
              :disabled="isGenerating || isSaving" 
              placeholder="e.g., tech, gaming, cooking, education"
              :value="domain"
              @input="domain = $event.value"
            />
          </div>

          <div class="form-actions">
            <button class="purple-btn generate-btn" 
                    :disabled="isGenerating || isSaving || !title || !domain" 
                    type="submit">
                <span class="spinner" v-if="isGenerating"/>
                <span class="btn-text">{{isGenerating ? 'Generating...' : 'Generate Thumbnail'}}</span>
            </button>
          </div>
        </form>
      </div>

      <!-- Results Section -->
      <div v-if="result" class="result-container">
        <div class="generated-thumbnail-section">
          <h2 class="section-title">Generated Thumbnail</h2>
          <div class="thumbnail-display">
            <img :src="imageData" alt="Generated thumbnail" class="main-thumbnail" />
            <div class="thumbnail-info">
              <div class="info-item">
                <span class="info-label">Title:</span>
                <span class="info-value">{{ title }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Domain:</span>
                <span class="info-value">{{ domain }}</span>
              </div>
              <button class="action-btn download-btn" @click="handleThumbnailDownload">
                <i class="icon-download" /> Download
              </button>
            </div>
          </div>
        </div>

        <div class="scraped-thumbnails-section">
          <h2 class="section-title">Reference Thumbnails</h2>
          <p class="section-description">Scraped thumbnails from YouTube for inspiration</p>
          <div class="thumbnails-grid">
            <div v-for="(item, index) in scrapedThumbnails" 
                :key="index"
                class="scraped-thumbnail-card">
              <img :src="item.thumbnail" 
                  alt="YouTube thumbnail"
                  class="scraped-thumbnail-image"
              />
              <div class="scraped-thumbnail-info">
                <span class="video-title">{{ item.title }}</span>
                <a :href="item.videoUrl" target="_blank" class="action-btn mini-btn">
                  <i class="icon-external" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import BaseInput from "@/components/BaseInput.vue";
  import {computed, ref} from "vue";
  import {useStore} from "vuex";

  const { state, dispatch, commit } = useStore()

  dispatch('get_youtube_thumbnail_prompts')

  const isFetching = computed(() => state.reqStatus === "fetching");
  const isSaving = computed(() => state.reqStatus === "sending");
  const isGenerating = computed(() => state.reqStatus === "generating");
  const result = computed(() => state.youtubeThumbnail.result)
  const imageData = computed(() => {
    if (!result.value) return null;
    const imgType = 'image/jpeg'; 
    return `data:${imgType};base64,${result.value.imageData}`;
  });
  const title = ref('')
  const domain = ref('')
  const scrapedThumbnails = computed(() => result.value?.thumbnails || []);

  const _promptGenerateTitleFromYoutube = ref('')
  const promptGenerateTitleFromYoutube = computed({
    get: () => _promptGenerateTitleFromYoutube.value || state.youtubeThumbnail.prompts?.promptGenerateTitleFromYoutube || '',
    set: (value) => {
      _promptGenerateTitleFromYoutube.value = value
      commit('SET_YT_THUMBNAIL_STATE', {key: 'prompts', value: {...state.youtubeThumbnail.prompts, promptGenerateTitleFromYoutube: value}})
    }
  })


  // Prompt Image Generate
  const _promptImageGenerate = ref('')
  const promptImageGenerate = computed({
    get: () => _promptImageGenerate.value || state.youtubeThumbnail.prompts?.promptImageGenerate || '',
    set: (value) => {
      _promptImageGenerate.value = value
      commit('SET_YT_THUMBNAIL_STATE', {key: 'prompts', value: {...state.youtubeThumbnail.prompts, promptImageGenerate: value}})
    }
  })

  // Prompt Face Centric
  const _promptFaceCentric = ref('')
  const promptFaceCentric = computed({
    get: () => _promptFaceCentric.value || state.youtubeThumbnail.prompts?.promptFaceCentric || '',
    set: (value) => {
      _promptFaceCentric.value = value
      commit('SET_YT_THUMBNAIL_STATE', {key: 'prompts', value: {...state.youtubeThumbnail.prompts, promptFaceCentric: value}})
    }
  })

  // Prompt Curiosity Hook
  const _promptCuriosityHook = ref('')
  const promptCuriosityHook = computed({
    get: () => _promptCuriosityHook.value || state.youtubeThumbnail.prompts?.promptCuriosityHook || '',
    set: (value) => {
      _promptCuriosityHook.value = value
      commit('SET_YT_THUMBNAIL_STATE', {key: 'prompts', value: {...state.youtubeThumbnail.prompts, promptCuriosityHook: value}})
    }
  })

  // Prompt Cinematic
  const _promptCinematic = ref('')
  const promptCinematic = computed({
    get: () => _promptCinematic.value || state.youtubeThumbnail.prompts?.promptCinematic || '',
    set: (value) => {
      _promptCinematic.value = value
      commit('SET_YT_THUMBNAIL_STATE', {key: 'prompts', value: {...state.youtubeThumbnail.prompts, promptCinematic: value}})
    }
  })

  // Prompt Collage
  const _promptCollage = ref('')
  const promptCollage = computed({
    get: () => _promptCollage.value || state.youtubeThumbnail.prompts?.promptCollage || '',
    set: (value) => {
      _promptCollage.value = value
      commit('SET_YT_THUMBNAIL_STATE', {key: 'prompts', value: {...state.youtubeThumbnail.prompts, promptCollage: value}})
    }
  })

  // Prompt Minimalist Headshot
  const _promptMinimalistHeadshot = ref('')
  const promptMinimalistHeadshot = computed({
    get: () => _promptMinimalistHeadshot.value || state.youtubeThumbnail.prompts?.promptMinimalistHeadshot || '',
    set: (value) => {
      _promptMinimalistHeadshot.value = value
      commit('SET_YT_THUMBNAIL_STATE', {key: 'prompts', value: {...state.youtubeThumbnail.prompts, promptMinimalistHeadshot: value}})
    }
  })

  function handleSavePrompts() {
    dispatch('update_youtube_thumbnail_prompts')
  }

  function handleGenerateThumbnail() {
    dispatch('generate_youtube_thumbnail', {
      title: title.value,
      domain: domain.value
    })
  }

  function handleThumbnailDownload() {
    if (!imageData.value) return;
    
    const mimeType = imageData.value.split(';')[0].split(':')[1];
    let fileExtension = 'jpg';
    
    if (mimeType === 'image/png') {
      fileExtension = 'png';
    } else if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') {
      fileExtension = 'jpg';
    } else if (mimeType === 'image/webp') {
      fileExtension = 'webp';
    }
    
    const sanitizedTitle = title.value.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const fileName = `${sanitizedTitle}_thumbnail_${timestamp}.${fileExtension}`;
    
    const downloadLink = document.createElement('a');
    downloadLink.href = imageData.value;
    downloadLink.download = fileName;
    
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }
</script>

<style lang="scss">
  .youtube-thumbnail-generator {
    @media only screen and (min-width: 0) {
      padding-bottom: 16px;

      .prompts-section,
      .generation-section {
        background-color: #fff;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        padding: 32px;
        margin-bottom: 32px;
        border: 1px solid #e2e8f0;
      }

      .prompts-form {
        .prompts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
          gap: 24px;
          margin-bottom: 32px;

          @media (max-width: 768px) {
            grid-template-columns: 1fr;
          }
        }

        .prompt-group {
          background-color: #f8fafc;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          transition: border-color 0.2s ease;

          &:hover {
            border-color: #cbd5e0;
          }

          textarea {
            min-height: 120px;
            font-size: 14px;
            line-height: 1.5;
          }
        }
      }

      .generation-form {
        .input-group {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 20px;
          margin-bottom: 24px;

          @media (max-width: 768px) {
            grid-template-columns: 1fr;
          }
        }
      }

      .form-actions {
        display: flex;
        justify-content: flex-end;
        padding-top: 16px;
        border-top: 1px solid #e2e8f0;

        .purple-btn {
          padding: 8px 16px;
          min-width: 160px;
          height: 48px;
          font-size: 16px;
          font-weight: 600;
          border-radius: 8px;
          transition: all 0.2s ease;
          display: flex;
          justify-content: center;
          align-items: center;

          &.save-btn {
            background: #7e3af2;
          }

          &.generate-btn {
            background: #7e3af2;
          }

          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 6px 12px rgba(126, 58, 242, 0.3);
          }
        }
      }

      .loading-message {
        display: flex;
        align-items: center;
        font-size: 16px;
        color: #666;
        justify-content: center;
        padding: 40px;
        background-color: #f8fafc;
        border-radius: 8px;
        margin: 20px 0;
      }

      .spinner {
        display: inline-block;
        width: 20px;
        height: 20px;
        margin-right: 10px;
        border-radius: 50%;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: #fff;
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      .result-container {
        margin-top: 40px;
        display: flex;
        flex-direction: column;
        gap: 40px;
      }

      .section-title {
        font-size: 24px;
        font-weight: 700;
        margin-bottom: 8px;
        color: #1a202c;
        position: relative;
        
        &:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -8px;
          width: 50px;
          height: 4px;
          background: linear-gradient(45deg, #7e3af2, #a855f7);
          border-radius: 2px;
        }
      }

      .section-description {
        font-size: 16px;
        color: #64748b;
        margin-bottom: 24px;
        line-height: 1.6;
      }

      .generated-thumbnail-section,
      .scraped-thumbnails-section {
        background-color: #fff;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        padding: 32px;
        border: 1px solid #e2e8f0;
      }

      .thumbnail-display {
        display: flex;
        flex-direction: column;
        gap: 24px;
        
        @media (min-width: 768px) {
          flex-direction: row;
          align-items: flex-start;
        }
      }

      .main-thumbnail {
        width: 100%;
        max-width: 480px;
        border-radius: 8px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        transition: transform 0.3s ease, box-shadow 0.3s ease;

        &:hover {
          transform: scale(1.02);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }
      }

      .thumbnail-info {
        padding: 20px;
        background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        border-radius: 12px;
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 16px;
        border: 1px solid #e2e8f0;
      }

      .info-item {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .info-label {
        font-size: 13px;
        color: #64748b;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .info-value {
        font-size: 16px;
        color: #1a202c;
        font-weight: 600;
        line-height: 1.4;
      }

      .thumbnails-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 24px;
      }

      .scraped-thumbnail-card {
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        transition: all 0.3s ease;
        border: 1px solid #e2e8f0;
        
        &:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
          border-color: #cbd5e0;
        }
      }

      .scraped-thumbnail-image {
        width: 100%;
        height: 160px;
        object-fit: cover;
        transition: transform 0.3s ease;
      }

      .scraped-thumbnail-card:hover .scraped-thumbnail-image {
        transform: scale(1.05);
      }

      .scraped-thumbnail-info {
        padding: 16px;
        background-color: #fff;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
      }

      .video-title {
        font-size: 14px;
        font-weight: 600;
        color: #2d3748;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        flex: 1;
        
        &:hover {
          color: #7e3af2;
        }
      }

      .action-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 10px 18px;
        background: linear-gradient(45deg, #7e3af2, #a855f7);
        color: white;
        border: none;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        gap: 8px;
        margin-top: 16px;
        align-self: flex-start;
        
        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 12px rgba(126, 58, 242, 0.3);
        }
      }

      .mini-btn {
        padding: 8px;
        margin: 0;
        background-color: #f1f5f9;
        color: #64748b;
        min-width: auto;
        
        &:hover {
          background-color: #e2e8f0;
          color: #7e3af2;
          transform: translateY(-1px);
        }
      }

      .icon-download:before {
        content: "⬇";
        font-size: 16px;
      }

      .icon-external:before {
        content: "↗";
        font-size: 14px;
      }
    }
  }
</style>